import type { Dimension, Offer, Phrase, Segment } from '@dashboard/types/config'

/**
 * Personalized offer variants.
 *
 * A variant is one combination of answers, one per question or segment group that the offer's phrases depend on.
 * Every group also has a "Not known" answer (visitors we haven't segmented), so a group with five answers contributes
 * six options and the variant count is the product across groups. Resolution mirrors the snippet's phrase source
 * (`resources/js/Snippet/sources/phrases.js`): a mapped, non-empty value for the answer wins, otherwise the fallback.
 */

export type OfferPart = 'title' | 'description' | 'buttonText' | 'imageUrl'
export const OFFER_PARTS: readonly OfferPart[] = ['title', 'description', 'buttonText', 'imageUrl']

/** Encodes "Not known" in combination keys and URLs. Segment IDs never collide with it (`seg_*`). */
export const NOT_KNOWN_KEY = 'unknown'
export const NOT_KNOWN_LABEL = 'Not known'

export type VariantAnswer = { segmentId: string | null; label: string }
export type VariantGroup = { id: string; name: string; answers: VariantAnswer[] }
/** Group ID → answer segment ID, or null for "Not known". */
export type VariantCombination = Record<string, string | null>
/** Group ID → the only answer to keep (segment ID, or null for "Not known"). Absent groups mean "Any". */
export type VariantFilters = Record<string, string | null>
export type PersonalizedField = { part: OfferPart; phraseId: string; segmentGroupId: string }

/** A `{{ phrases.<id> }}` reference; capture group 1 is the phrase ID. */
export const PHRASE_PATTERN = /{{\s*phrases\.(\w+)\s*}}/g

export function phraseIdsIn(text: string | null | undefined): string[] {
  if (!text) {
    return []
  }
  const ids: string[] = []
  for (const match of text.matchAll(PHRASE_PATTERN)) {
    const id = match[1]
    if (id !== undefined && !ids.includes(id)) {
      ids.push(id)
    }
  }
  return ids
}

/** Every phrase occurrence that can change by answer, in part order, at most once per part. */
export function personalizedFields(offer: Offer, phrases: Phrase[], dimensions: Dimension[]): PersonalizedField[] {
  const fields: PersonalizedField[] = []
  for (const part of OFFER_PARTS) {
    for (const phraseId of phraseIdsIn(offer.parts[part])) {
      const segmentGroupId = phrases.find((phrase) => phrase.id === phraseId)?.segmentGroupId
      if (segmentGroupId && dimensions.some((dimension) => dimension.id === segmentGroupId)) {
        fields.push({ part, phraseId, segmentGroupId })
      }
    }
  }
  return fields
}

/** Groups in order of first use, each answering "Not known" first and then its answers in configured order. */
export function variantGroups(
  offer: Offer,
  phrases: Phrase[],
  dimensions: Dimension[],
  answerLabel: (segment: Segment, dimension: Dimension) => string = (segment) => segment.name
): VariantGroup[] {
  const groups: VariantGroup[] = []
  for (const field of personalizedFields(offer, phrases, dimensions)) {
    if (groups.some((group) => group.id === field.segmentGroupId)) {
      continue
    }
    const dimension = dimensions.find((candidate) => candidate.id === field.segmentGroupId)
    if (!dimension) {
      continue
    }
    groups.push({
      id: dimension.id,
      name: dimension.name,
      answers: [
        { segmentId: null, label: NOT_KNOWN_LABEL },
        ...dimension.segments.map((segment) => ({ segmentId: segment.id, label: answerLabel(segment, dimension) }))
      ]
    })
  }
  return groups
}

/** Attribution prefers mapped answers like the snippet, without reordering the variant deck. */
export function resultRequestGroups(offer: Offer, phrases: Phrase[], groups: VariantGroup[]) {
  const referenced = new Set(OFFER_PARTS.flatMap((part) => phraseIdsIn(offer.parts[part])))
  const mappedByGroup = new Map<string, Set<string>>()
  for (const phrase of phrases) {
    if (!referenced.has(phrase.id) || !phrase.segmentGroupId) {
      continue
    }
    const mapped = mappedByGroup.get(phrase.segmentGroupId) ?? new Set<string>()
    for (const segmentId of Object.keys(phrase.map)) {
      mapped.add(segmentId)
    }
    mappedByGroup.set(phrase.segmentGroupId, mapped)
  }
  return groups.map((group) => {
    const mapped: string[] = []
    const unmapped: string[] = []
    for (const { segmentId } of group.answers) {
      if (segmentId) {
        const segments = mappedByGroup.get(group.id)?.has(segmentId) ? mapped : unmapped
        segments.push(segmentId)
      }
    }
    return { id: group.id, segments: [...mapped, ...unmapped] }
  })
}

export function variantCount(groups: VariantGroup[]): number {
  return groups.reduce((count, group) => count * group.answers.length, 1)
}

/** The combination at a position, with the first group varying slowest. Position 0 is everyone "Not known". */
export function combinationAt(groups: VariantGroup[], index: number): VariantCombination {
  const combination: VariantCombination = {}
  let remaining = Math.max(0, Math.floor(index)) % variantCount(groups)
  for (const group of [...groups].reverse()) {
    const answer = group.answers[remaining % group.answers.length]
    combination[group.id] = answer ? answer.segmentId : null
    remaining = Math.floor(remaining / group.answers.length)
  }
  return combination
}

/** Position of a combination, or -1 when it names an answer a group doesn't have. */
export function indexOfCombination(groups: VariantGroup[], combination: VariantCombination): number {
  let index = 0
  for (const group of groups) {
    const answerIndex = group.answers.findIndex((answer) => answer.segmentId === (combination[group.id] ?? null))
    if (answerIndex === -1) {
      return -1
    }
    index = index * group.answers.length + answerIndex
  }
  return index
}

/** Groups narrowed to their filtered answer; enumerate the result to walk only matching variants. */
export function filterGroups(groups: VariantGroup[], filters: VariantFilters): VariantGroup[] {
  return groups.map((group) => {
    if (!Object.prototype.hasOwnProperty.call(filters, group.id)) {
      return group
    }
    return { ...group, answers: group.answers.filter((answer) => answer.segmentId === filters[group.id]) }
  })
}

export function combinationKey(groups: VariantGroup[], combination: VariantCombination): string {
  return groups.map((group) => combination[group.id] ?? NOT_KNOWN_KEY).join('|')
}

/** Inverse of `combinationKey`; null when the key doesn't fit the current groups. */
export function parseCombinationKey(groups: VariantGroup[], key: string): VariantCombination | null {
  if (groups.length === 0) {
    return key === '' ? {} : null
  }
  const parts = key.split('|')
  if (parts.length !== groups.length) {
    return null
  }
  const combination: VariantCombination = {}
  for (const [index, group] of groups.entries()) {
    const part = parts[index]
    const segmentId = part === NOT_KNOWN_KEY ? null : (part ?? null)
    if (!group.answers.some((answer) => answer.segmentId === segmentId)) {
      return null
    }
    combination[group.id] = segmentId
  }
  return combination
}

export function answerFor(group: VariantGroup, combination: VariantCombination): VariantAnswer {
  const segmentId = combination[group.id] ?? null
  return group.answers.find((answer) => answer.segmentId === segmentId) ?? { segmentId: null, label: NOT_KNOWN_LABEL }
}

/** What a phrase renders for a combination; a group missing from the combination counts as "Not known". */
export function phraseTextFor(phrase: Phrase, combination: VariantCombination): string {
  const segmentId = phrase.segmentGroupId ? (combination[phrase.segmentGroupId] ?? null) : null
  return (segmentId !== null && phrase.map[segmentId]) || phrase.fallbackText
}

/** Phrase ID → rendered text for every phrase, for forcing a variant in previews. */
export function phraseTextMap(phrases: Phrase[], combination: VariantCombination): Record<string, string> {
  return Object.fromEntries(phrases.map((phrase) => [phrase.id, phraseTextFor(phrase, combination)]))
}
